import React, { useState, useRef } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Box, ThemeProvider } from '@mui/material';
import { setLoginId, setFirstLoginReturn, setLoginReturn, addPopup } from '../../../../redux/actions';
import LoginManager from './LoginManager';
import { generateTestId } from '../../../../app/functions';
import { appTheme } from 'common/theme';
import { ModalHot } from 'components/common/ModalHot';
import { PhoneValidationModal } from 'components/common/PhoneValidationModal';
import { GeneralModal } from 'components/common/GeneralModal';
import { GENERAL_MESSAGE } from '../../../../constants/popup-types';

const LoginPopup = (props) => {
    const { 
        open, 
        onClose, 
        testID = 'login-popup', 
        setLoginId,
        setFirstLoginReturn,
        setLoginReturn,
        addPopup,
        loginFormName,
        firstLogin,
        userData,
        onCloseNotLoggedInUser,
        redirectPathWhenNotLoggedIn,
    } = props;
    const history = useHistory();

    const isLoginSuccessfulRef = useRef(false);
    const [isOtpActive, setIsOtpActive] = useState(false);
    const [wasOtpActivated, setWasOtpActivated] = useState(false);
    const [otpData, setOtpData] = useState(null);
    const [responseModalOpen, setResponseModalOpen] = useState(false);
    const [responseMessage, setResponseMessage] = useState('');
    const [shouldHideModalContent, setShouldHideModalContent] = useState(false);
    const [personalInfoFormModalOpen, setPersonalInfoFormModalOpen] = useState(false);

    const handleClose = () => {
        setLoginId('');
        setFirstLoginReturn(false);
        setLoginReturn(false);
        setIsOtpActive(false);
        setWasOtpActivated(false);
        setOtpData(null);
        setShouldHideModalContent(false);
        setPersonalInfoFormModalOpen(false);
        
        if (!isLoginSuccessfulRef.current && !userData) {
            if (onCloseNotLoggedInUser) {
                onCloseNotLoggedInUser();
            } else if (redirectPathWhenNotLoggedIn) {
                history.push(redirectPathWhenNotLoggedIn);
            }
        }
        isLoginSuccessfulRef.current = false;
        
        onClose();
    };

    const handleOtpRequest = (data) => {
        setOtpData(data);
        setIsOtpActive(true);
        setWasOtpActivated(true);
    };

    const handleOtpClose = () => {
        handleClose();
    };

    const handleVerificationSuccess = async (otpResult) => {
        isLoginSuccessfulRef.current = true;
        
        if (otpData?.onVerificationSuccess) {
            await otpData.onVerificationSuccess(otpResult);
        }
    };

    const handleRegistrationResponse = ({ type, message }) => {
        if (type === 'success') {
            isLoginSuccessfulRef.current = true;
            setIsOtpActive(false);
            setWasOtpActivated(false);
            setOtpData(null);
            setShouldHideModalContent(false);
            setPersonalInfoFormModalOpen(false);
            setResponseModalOpen(false);
            setLoginId('');
            setFirstLoginReturn(false);
            setLoginReturn(false);
            onClose();
            addPopup({
                type: GENERAL_MESSAGE,
                text: message,
                btnText: 'אישור',
            });
            return;
        }

        setResponseMessage(message);
        setResponseModalOpen(true);
        setIsOtpActive(false);
        setShouldHideModalContent(true);
        setOtpData(null);
    };

    const handleCloseAllModals = (closeCompletely = false) => {
        // Close PhoneValidationModal
        setIsOtpActive(false);
        setWasOtpActivated(false);
        setOtpData(null);
        
        if (closeCompletely) {
            // Close everything completely (when GeneralModal closes)
            setPersonalInfoFormModalOpen(false);
            handleClose();
        } else {
            setShouldHideModalContent(true);
            setPersonalInfoFormModalOpen(true);
        }
    };

    const isFullRegistrationForm = loginFormName === 'registration_form' && (firstLogin?.userData || firstLogin?.id);
    
    return (
        <ThemeProvider theme={appTheme}>
            <ModalHot
                open={open && !shouldHideModalContent && !responseModalOpen && !personalInfoFormModalOpen}
                onClose={handleClose}
                maxWidth={isFullRegistrationForm ? 1200 : 600}
                testID={generateTestId(testID, 'modal')}
                showCloseButton={!isOtpActive}
            >
                {() => (
                    <Box 
                        sx={{ 
                            width: '100%',
                            display: (isOtpActive || wasOtpActivated) ? 'none' : 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                        }} 
                    >
                        {!wasOtpActivated && (
                            <LoginManager 
                                {...props}
                                onOtpStateChange={setIsOtpActive}
                                renderPhoneValidationExternally={true}
                                onOtpRequest={handleOtpRequest}
                                onCloseModal={handleClose}
                                onRegistrationResponse={handleRegistrationResponse}
                                onCloseAllModals={handleCloseAllModals}
                            />
                        )}
                    </Box>
                )}
            </ModalHot>

            {otpData && (
                <PhoneValidationModal
                    open={isOtpActive}
                    onClose={handleOtpClose}
                    phone={otpData.phone}
                    userId={otpData.userId}
                    email={otpData.email}
                    initialOtpResponse={otpData.otpResponse}
                    onVerificationSuccess={handleVerificationSuccess}
                    process="register"
                    testID={generateTestId(testID, 'phone-validation-modal')}
                />
            )}

            <GeneralModal 
                open={responseModalOpen} 
                onClose={() => {
                    setResponseModalOpen(false);
                    // Close all modals and prevent going back
                    handleClose();
                }} 
                buttonText="אישור" 
                testID={generateTestId(testID, 'response-modal')}
            >
                {responseMessage}
            </GeneralModal>
        </ThemeProvider>
    );
};

const mapStateToProps = store => ({
    deviceState: store.state.deviceState,
    text: store.config.Translations,
    loginFormName: store.state.loginFormName,
    login: store.config.LoginReturn,
    firstLogin: store.config.FirstLoginReturn,
    banners: store.config.BannersArr,
    userData: store.config.UserData,
});

const mapDispatchToProps = dispatch => ({
    setLoginId: (payload) => dispatch(setLoginId(payload)),
    setFirstLoginReturn: (payload) => dispatch(setFirstLoginReturn(payload)),
    setLoginReturn: (payload) => dispatch(setLoginReturn(payload)),
    addPopup: (payload) => dispatch(addPopup(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(LoginPopup);